import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Calculator, Link2, Copy, Check, Loader2, Save, Users2 } from "lucide-react";
import { api, apiError, eur } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const field = "w-full rounded-lg border border-input bg-white px-3 py-2 text-sm text-forest outline-none focus:border-forest";
const Opt = ({ on, onClick, testid, children }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`rounded-xl border px-3 py-3 text-sm font-medium transition-colors ${on ? "border-forest bg-sage text-forest" : "border-border text-forest/80 hover:bg-sage/50"}`}>{children}</button>
);

// Calcolatore Eticapro — Formula Self (Protezione Famiglia) dentro il portale, sullo schema di Formula Service.
export default function FormulaSelfCalc() {
  const { user, isAdmin } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const editId = params.get("edit");
  const [cfg, setCfg] = useState(null);
  const [pacchetto, setPacchetto] = useState("maxi");
  const [nucleo, setNucleo] = useState("nucleo_intero");
  const [disab, setDisab] = useState(false);
  const [dataEffetto, setDataEffetto] = useState(new Date().toISOString().slice(0, 10));
  const [anagrafiche, setAnagrafiche] = useState([]);
  const [anagraficaId, setAnagraficaId] = useState(params.get("anagrafica") || "");
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/public/protezione-famiglia/config").then(({ data }) => setCfg(data));
    api.get("/anagrafiche").then(({ data }) => setAnagrafiche(data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!editId) return;
    api.get(`/quotes/${editId}`).then(({ data: q }) => {
      const i = q.pf_input || {};
      setAnagraficaId(q.anagrafica_id || "");
      if (i.pacchetto) setPacchetto(i.pacchetto);
      if (i.nucleo) setNucleo(i.nucleo);
      setDisab(!!i.disabilita_grave);
    }).catch((e) => toast.error(apiError(e)));
  }, [editId]);

  if (!cfg) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;
  const pkg = cfg.packages.find((p) => p.code === pacchetto);
  const premio = pkg?.premi[nucleo];
  const gLabel = (c) => cfg.garanzie.find((g) => g.code === c)?.label || c;

  const genLink = () => {
    const qs = new URLSearchParams({ pacchetto, nucleo, ...(user?.codice_collab ? { ref: user.codice_collab } : {}) });
    setLink(`${window.location.origin}/protezione-famiglia?${qs.toString()}`);
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); } catch { window.prompt("Copia il link", link); }
    setCopied(true); toast.success("Link copiato"); setTimeout(() => setCopied(false), 2000);
  };
  const save = async () => {
    if (!anagraficaId) return toast.error("Seleziona il cliente");
    setSaving(true);
    try {
      const { data } = await api.post("/protezione-famiglia/quotes", { pacchetto, nucleo, disabilita_grave: disab, data_effetto: dataEffetto, anagrafica_id: anagraficaId, quote_id: editId || undefined });
      toast.success(`Preventivo ${data.codice_prev || ""} salvato nel fascicolo`);
      navigate(isAdmin ? `/admin/preventivi?open=${data.id}` : `/preventivi/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="formula-self-calc">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-3xl font-semibold text-forest"><Calculator className="h-7 w-7" />Calcolatore Eticapro — Formula Self</h1>
        <p className="mt-1 text-sm text-muted-foreground">Eticapro Infortuni Assimoco — Protezione Famiglia H24 · premi lordi annui per pacchetto e composizione del nucleo.</p>
      </div>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0 space-y-5">
          <Card className="rounded-xl border-border/60 p-5">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-forest"><Users2 className="h-4 w-4" />Composizione familiare</h2>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {cfg.nuclei.map((n) => <Opt key={n.code} on={nucleo === n.code} onClick={() => setNucleo(n.code)} testid={`fs-nucleo-${n.code}`}>{n.label}</Opt>)}
            </div>
          </Card>
          <Card className="rounded-xl border-border/60 p-5">
            <h2 className="mb-3 text-sm font-semibold text-forest">Pacchetto</h2>
            <div className="grid gap-3 sm:grid-cols-3">
              {cfg.packages.map((p) => (
                <Opt key={p.code} on={pacchetto === p.code} onClick={() => setPacchetto(p.code)} testid={`fs-pacchetto-${p.code}`}>
                  <div className="font-semibold">{p.nome}</div><div className="mt-1 text-xs text-muted-foreground">{eur(p.premi[nucleo])} / anno</div>
                </Opt>
              ))}
            </div>
            {pkg && (
              <div className="mt-4 divide-y rounded-lg border text-sm" data-testid="fs-capitali">
                {Object.entries(pkg.capitali).map(([k, v]) => <div key={k} className="flex justify-between px-3 py-1.5"><span className="text-muted-foreground">{gLabel(k)}</span><span className="text-forest">{eur(v)}</span></div>)}
              </div>
            )}
          </Card>
          <Card className="space-y-4 rounded-xl border-border/60 p-5">
            <div className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2.5">
              <Label>Nel nucleo c'è una persona con disabilità grave (vantaggi fiscali)</Label>
              <Switch checked={disab} onCheckedChange={setDisab} data-testid="fs-disabilita" />
            </div>
            <label className="block text-xs text-muted-foreground">Cliente (fascicolo)
              <select className={field} value={anagraficaId} onChange={(e) => setAnagraficaId(e.target.value)} disabled={!!editId} data-testid="fs-anagrafica">
                <option value="">Seleziona il cliente…</option>{anagrafiche.map((a) => <option key={a.id} value={a.id}>{a.ragione_sociale}</option>)}
              </select></label>
            <div className="space-y-1.5"><Label>Data di effetto</Label><Input type="date" value={dataEffetto} onChange={(e) => setDataEffetto(e.target.value)} data-testid="fs-data-effetto" /></div>
          </Card>
        </div>
        <div className="min-w-0 space-y-5">
          <Card className="overflow-hidden rounded-xl border-border/60">
            <div className="bg-forest p-5 text-white">
              <div className="text-[11px] font-bold uppercase tracking-wider text-white/70">Premio lordo annuo</div>
              <div className="font-heading text-4xl font-semibold" data-testid="fs-premio">{eur(premio)}</div>
            </div>
            <div className="space-y-1 p-5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Pacchetto</span><span className="text-forest">{pkg?.nome}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Nucleo</span><span className="text-forest">{cfg.nuclei.find((n) => n.code === nucleo)?.label}</span></div>
              {!isAdmin || editId ? (
                <button onClick={save} disabled={saving || !anagraficaId} data-testid="fs-save"
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border-2 border-forest py-2.5 text-sm font-bold uppercase tracking-wide text-forest hover:bg-sage disabled:opacity-50">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}{editId ? "Aggiorna nel fascicolo" : "Salva nel fascicolo"}
                </button>
              ) : null}
            </div>
          </Card>
          <Card className="rounded-xl border-border/60 p-5">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-forest"><Link2 className="h-4 w-4" />Link per il cliente</h2>
            <p className="mt-1 text-xs text-muted-foreground">Apre la pagina Formula Self già impostata su pacchetto e nucleo: il cliente legge le garanzie e richiede la proposta.</p>
            <button onClick={genLink} data-testid="fs-genera-link" className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-[#0E8C84] py-2.5 text-sm font-semibold text-white hover:opacity-90"><Link2 className="h-4 w-4" />Genera link cliente</button>
            {link && (
              <div className="mt-3 flex gap-2">
                <Input readOnly value={link} data-testid="fs-link" className="text-xs" />
                <button onClick={copy} data-testid="fs-copy-link" className="rounded-lg border px-3 text-forest hover:bg-sage">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</button>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
